import { ref, onMounted, onUnmounted } from "vue";
import { logInfo } from "@/helpers/logError";

/**
 * Detects device orientation changes and exposes a reactive counter that increments
 * once per change after the browser layout has settled (~100 ms).
 *
 * iOS/iPadOS fires `orientationchange` before the reflow is complete, so components
 * that read DOM dimensions must wait before acting. The 100 ms debounce lets the
 * browser settle before consumers re-read layout (e.g. scroll heights, menu positions).
 *
 * Registers both `screen.orientation.change` (modern) and `window.orientationchange`
 * (older browsers / iOS). Both may fire for the same physical rotation; the debounce
 * collapses any duplicates into a single counter increment. Does NOT listen to
 * `window.resize` because resize also fires on virtual-keyboard show/hide and desktop
 * window dragging.
 *
 * Options API components can consume this from their setup() hook and watch
 * `orientationChangeCount` in the Options API watch block.
 *
 * @see LSP-16440, LSP-16441
 */
export function useOrientationChange() {
	const orientationChangeCount = ref(0);
	let debounceTimer: ReturnType<typeof setTimeout> | null = null;

	function handleChange() {
		if (debounceTimer !== null) {
			clearTimeout(debounceTimer);
		}
		debounceTimer = setTimeout(() => {
			debounceTimer = null;
			orientationChangeCount.value += 1;
			// Diagnostic: this fix targets a SILENT failure (rotation happened but the UI
			// did not refresh). Per CLAUDE.md, log every settled change — and which
			// listener path delivered it — so a future regression (events never firing,
			// debounce mistuned) leaves a Datadog trail instead of repeating the
			// rotate-by-hand investigation. This fires only on an actual rotation (rare),
			// not on mount, so it does not flood Datadog on every dialog open. logInfo
			// (not console.log) is required — admin forwards only error/warn to Datadog.
			logInfo("useOrientationChange: orientation change settled", {
				count: orientationChangeCount.value,
				hasScreenOrientation: Boolean(window.screen?.orientation)
			});
		}, 100);
	}

	onMounted(() => {
		if (window.screen?.orientation) {
			window.screen.orientation.addEventListener("change", handleChange);
		}
		window.addEventListener("orientationchange", handleChange);
	});

	onUnmounted(() => {
		if (debounceTimer !== null) {
			clearTimeout(debounceTimer);
			debounceTimer = null;
		}
		if (window.screen?.orientation) {
			window.screen.orientation.removeEventListener("change", handleChange);
		}
		window.removeEventListener("orientationchange", handleChange);
	});

	return { orientationChangeCount };
}
